import { useEffect, useRef } from 'react';

type EscapeHandler = (event: KeyboardEvent) => void;

interface Entry {
  /** Monotonically-increasing ID assigned at the FIRST render of each
   *  hook instance. Used as the priority when dispatching: larger ID =
   *  more recent layer. */
  readonly id: number;
  readonly handler: EscapeHandler;
}

/** Registered modal layers. The handler with the LARGEST `id` wins —
 *  this matches "most-recently-opened layer responds to Esc" for both
 *  sequential opens (Dialog opens, then user opens Popover inside) and
 *  simultaneous mounts (both render with `open=true` on first paint).
 *
 *  Why a module-level dispatcher instead of N document listeners:
 *    Each open modal previously attached its own `document.keydown`
 *    listener and called `e.stopPropagation()`. But all listeners on the
 *    *same* EventTarget (document) fire regardless of stopPropagation —
 *    `stopPropagation` only halts BUBBLING between nodes, not between
 *    listeners on a single node. The outcome was that pressing Escape
 *    inside a Popover-in-Dialog closed BOTH simultaneously.
 *
 *  Why a render-time counter rather than effect-mount order:
 *    React fires child effects BEFORE parent effects on mount, so a
 *    naive "last pushed wins" stack would incorrectly route Esc to the
 *    OUTER modal when both mount at the same paint. Render runs
 *    parent-then-child (top-down), so an ID issued in render reflects
 *    the natural nesting depth: child > parent. We use that ID as the
 *    dispatch priority, so the deeper layer always wins regardless of
 *    effect ordering. */
const entries: Entry[] = [];
let nextId = 0;

let installed = false;

function install(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (entries.length === 0) return;
    let top: Entry = entries[0] as Entry;
    for (let i = 1; i < entries.length; i += 1) {
      const candidate = entries[i] as Entry;
      if (candidate.id > top.id) top = candidate;
    }
    top.handler(e);
  });
}

/** Register an Escape-key handler that fires only when this layer is at
 *  the top of the modal stack. Stable across re-renders — the latest
 *  closure is always invoked via a ref, so callers don't need to memoize.
 *
 *  Pass `enabled = false` to opt out (e.g., `closeOnEscape={false}`);
 *  the layer doesn't register at all in that case, so its presence
 *  doesn't shadow a parent layer. */
export function useEscapeStack(handler: EscapeHandler, enabled: boolean): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // ID is allocated at render time so siblings/parents/children get IDs
  // in render order (parent < child), giving us the right "deepest
  // wins" priority regardless of which effect fires first.
  const idRef = useRef<number>(-1);
  if (idRef.current === -1) {
    nextId += 1;
    idRef.current = nextId;
  }

  useEffect(() => {
    if (!enabled) return undefined;
    install();
    const entry: Entry = {
      id: idRef.current,
      handler: (e) => {
        handlerRef.current(e);
      },
    };
    entries.push(entry);
    return () => {
      const idx = entries.indexOf(entry);
      if (idx >= 0) entries.splice(idx, 1);
    };
  }, [enabled]);
}

/** Test-only escape hatch: clear registered handlers and reset the ID
 *  counter between test cases. Not exported from the hooks index. */
export function __resetEscapeStackForTests(): void {
  entries.length = 0;
  nextId = 0;
}
